import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/ui';
import ExportButton from '../../components/ExportButton';
import ReportShare from '../../components/ReportShare';
import { fmtDate } from '../../utils/helpers';

// Desktop Brief Report: the four headline numbers, then one block per part of
// the brief. Each block's heading carries its checkbox - untick it and it
// leaves the screen and the exported file together. Logic: useBrief.
export default function BriefDesktop({ vm }) {
  const {
    canView, loading, blocks, toggleBlock, blockLabels, twoSided,
    functions, pastFunctions, fnOff, toggleFunction, shownFunctions,
    memberRows, memberTotals,
    invitedByRows, invitedByTotals,
    cardStats,
    moneyRows, moneyTotals, catOff, toggleCategory, withEstimates, setWithEstimates,
    bookingRows,
    summary, report, money, reload,
    listFor, openList, closeList, listReport, cardsToPrint, sweetsFunction,
  } = vm;
  const nav = useNavigate();
  // Right-click a line of the brief for the people behind its number: the
  // guests of that function, or everyone one member invited.
  const [menu, setMenu] = useState(null);     // { x, y, kind, id, label } | null
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [menu]);
  const openMenu = (e, kind, id, label) => {
    e.preventDefault();
    setMenu({ x: Math.min(e.clientX, window.innerWidth - 230), y: Math.min(e.clientY, window.innerHeight - 140), kind, id, label });
  };

  if (!canView) {
    return (
      <div>
        <div className="page-head"><h1>Brief Report</h1></div>
        <div className="card card-pad empty">The brief covers the whole event, so only an Event Admin can open it.</div>
      </div>
    );
  }

  const Head = ({ k, extra }) => (
    <div className="brief-head">
      <label className="brief-tick">
        <input type="checkbox" checked={blocks[k]} onChange={() => toggleBlock(k)} />
        <strong>{blockLabels[k]}</strong>
      </label>
      {extra}
    </div>
  );

  return (
    <div className="brief-page">
      <div className="page-head">
        <h1>Brief Report</h1>
        <div className="spacer" />
        <button className="btn light sm" onClick={reload}>↻ Refresh</button>
        <ExportButton report={report} label="⇪ Export brief" className="btn primary" />
      </div>

      <div className="mn-tiles">
        {summary.map(s => (
          <div key={s.label} className="mn-tile"><span>{s.label}</span><strong>{s.value}</strong></div>
        ))}
      </div>

      {menu && (
        <div className="ctx-menu" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
          <div className="ctx-title">{menu.label}</div>
          <button type="button" onClick={() => { openList(menu.kind, menu.id, menu.label); setMenu(null); }}>
            📄 Guest list — every person
          </button>
          {menu.kind === 'function' && (
            <>
              {/* One line per household, one sheet per job: a card, the gift
                  that goes with it and a packet of sweets each go to the
                  household - a family of three takes one, like a guest on
                  their own - but they are handed out on different days by
                  different people, so they are different lists. */}
              <button type="button" onClick={() => { openList('household', menu.id, menu.label, 'Cards'); setMenu(null); }}>
                📇 Invitation cards — one per household
              </button>
              <button type="button" onClick={() => { openList('household', menu.id, menu.label, 'Gifts'); setMenu(null); }}>
                🎁 Gifts — one per household
              </button>
              <button type="button" onClick={() => { openList('household', menu.id, menu.label, 'Sweets'); setMenu(null); }}>
                🍬 Sweets — one packet per household
              </button>
              <button type="button" onClick={() => { setMenu(null); nav(`/app/members?fn=${menu.id}`); }}>
                ↗ Open in Guests
              </button>
            </>
          )}
        </div>
      )}
      {listFor && listReport && <ReportShare {...listReport} onClose={closeList} />}

      {loading ? <div className="card card-pad"><Spinner /></div> : (
        <>
          {/* 1 — persons per function */}
          <div className="card brief-block">
            <Head k="members" extra={<span className="muted">
              Cards / gifts = one per household · Persons = what the caterer counts ·

              Tick the functions the brief should cover
              {pastFunctions.length > 0 && ` · ${pastFunctions.map(f => f.FunctionName).join(', ')} ${pastFunctions.length === 1 ? 'is' : 'are'} over and left out`}
            </span>} />
            {blocks.members && (
              <table className="tbl compact">
                <thead>
                  <tr>
                    <th style={{ width: 28 }} />
                    <th className="grow">Function</th><th>Date</th><th>Time</th>
                    <th className="mn-r" title="One per household — the invitation cards, and whatever goes with a card (a gift at the Thread Ceremony)">Cards / gifts</th>
                    <th className="mn-r">Parties</th><th className="mn-r">Named</th><th className="mn-r">Extra card</th>
                    <th className="mn-r" title="What the caterer counts">Persons (plates)</th>
                    {twoSided && <><th className="mn-r">Groom</th><th className="mn-r">Bride</th></>}
                  </tr>
                </thead>
                <tbody>
                  {memberRows.map(r => (
                    <tr key={r.key} className={r.on ? '' : 'brief-off'}
                      title="Right-click for the guest list of this function"
                      onContextMenu={(e) => openMenu(e, 'function', r.FunctionId, r.FunctionName)}>
                      <td><input type="checkbox" checked={r.on} onChange={() => toggleFunction(r.FunctionId)} /></td>
                      <td>{r.FunctionName}</td><td>{r.date}</td><td>{r.time || '—'}</td>
                      <td className="mn-r" title={`${r.households} households${r.extraCards ? ` + ${r.extraCards} extra cards` : ''}`}><strong>{r.cards}</strong></td>
                      <td className="mn-r">{r.parties}</td><td className="mn-r">{r.named}</td><td className="mn-r">{r.extra || ''}</td>
                      <td className="mn-r"><strong>{r.persons}</strong></td>
                      {twoSided && <><td className="mn-r">{r.groom || ''}</td><td className="mn-r">{r.bride || ''}</td></>}
                    </tr>
                  ))}
                  {memberRows.length === 0 && <tr><td colSpan={twoSided ? 11 : 9} className="empty">No functions yet.</td></tr>}
                </tbody>
                <tfoot>
                  <tr>
                    <td /><td colSpan={3}><strong>Total</strong></td>
                    <td className="mn-r"><strong>{memberTotals.cards}</strong></td>
                    <td className="mn-r">{memberTotals.parties}</td><td className="mn-r">{memberTotals.named}</td>
                    <td className="mn-r">{memberTotals.extra || ''}</td>
                    <td className="mn-r"><strong>{memberTotals.persons}</strong></td>
                    {twoSided && <><td className="mn-r">{memberTotals.groom || ''}</td><td className="mn-r">{memberTotals.bride || ''}</td></>}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* 2 — who invited how many */}
          <div className="card brief-block">
            <Head k="invitedBy" extra={<span className="muted">{invitedByRows.length} {invitedByRows.length === 1 ? 'person' : 'people'} have invited</span>} />
            {blocks.invitedBy && (
              <div className="table-wrap">
                <table className="tbl compact">
                  <thead>
                    <tr>
                      <th className="grow">Invited by</th><th>Mobile</th>
                      <th className="mn-r">Guests</th><th className="mn-r">Persons</th><th className="mn-r">Cards</th>
                      {shownFunctions.map(f => <th key={f.FunctionId} className="mn-r">{f.FunctionName}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {invitedByRows.map(r => (
                      <tr key={r.key} title="Right-click for the guests this person invited"
                        onContextMenu={(e) => openMenu(e, 'adder', r.key, r.name)}>
                        <td>{r.name}{r.isGuest && <span className="chip" style={{ marginLeft: 6 }}>Guest</span>}</td>
                        <td>{r.mobile || '—'}</td>
                        <td className="mn-r">{r.guests}</td><td className="mn-r"><strong>{r.persons}</strong></td>
                        <td className="mn-r">{r.totalCards}</td>
                        {shownFunctions.map(f => <td key={f.FunctionId} className="mn-r">{r.perFn[String(f.FunctionId)] || ''}</td>)}
                      </tr>
                    ))}
                    {invitedByRows.length === 0 && <tr><td colSpan={5 + shownFunctions.length} className="empty">No guests yet.</td></tr>}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>Total</strong></td>
                      <td className="mn-r">{invitedByTotals.guests}</td>
                      <td className="mn-r"><strong>{invitedByTotals.persons}</strong></td>
                      <td className="mn-r">{invitedByTotals.totalCards}</td>
                      {shownFunctions.map(f => (
                        <td key={f.FunctionId} className="mn-r">
                          {invitedByRows.reduce((s, r) => s + (r.perFn[String(f.FunctionId)] || 0), 0) || ''}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* 3 — cards to order */}
          <div className="card brief-block">
            <Head k="cards" extra={<>
              <span className="muted">{cardStats.households} households · <strong>{cardsToPrint} cards to print</strong> (one gift with each)</span>
              {/* The lists the distribution is run from, for the whole event -
                  a function's own lists are on its line in the first block. */}
              <button type="button" className="btn light sm" style={{ marginLeft: 'auto' }}
                onClick={() => openList('household', null, 'the whole event', 'Cards')}>📇 Card list</button>
              <button type="button" className="btn light sm"
                onClick={() => openList('household', null, 'the whole event', 'Gifts')}>🎁 Gift list</button>
              {/* One half-kg packet per household invited to the Thread
                  Ceremony, where they are handed out - a family takes one, a
                  guest on their own takes one; extra cards take none. */}
              {sweetsFunction && (
                <button type="button" className="btn light sm" title={`Households invited to ${sweetsFunction.FunctionName}`}
                  onClick={() => openList('household', sweetsFunction.FunctionId, sweetsFunction.FunctionName, 'Sweets')}>🍬 Sweets list</button>
              )}
            </>} />
            {blocks.cards && (
              <>
                <table className="tbl compact">
                  <thead>
                    <tr>
                      <th className="grow">Card / insert</th><th>Kind</th><th>Given for</th>
                      <th className="mn-r">Households</th><th className="mn-r">Extra cards</th><th className="mn-r">To order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cardStats.rows.map(r => (
                      <tr key={r.key}>
                        <td>{r.name}</td><td>{r.kind}</td><td className="muted">{r.forFunctions || '—'}</td>
                        <td className="mn-r">{r.households}</td><td className="mn-r">{r.extra || ''}</td>
                        <td className="mn-r"><strong>{r.total}</strong></td>
                      </tr>
                    ))}
                    {cardStats.rows.length === 0 && (
                      <tr><td colSpan={6} className="empty">No card types yet — set them up on the Cards page.</td></tr>
                    )}
                  </tbody>
                  {cardStats.rows.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={3}><strong>Total to order</strong></td>
                        <td className="mn-r">{cardStats.rows.reduce((s, r) => s + r.households, 0)}</td>
                        <td className="mn-r">{cardStats.rows.reduce((s, r) => s + r.extra, 0) || ''}</td>
                        <td className="mn-r"><strong>{cardStats.toOrder}</strong></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                <div className="muted brief-note">
                  {cardStats.extraCards ? `${cardStats.extraCards} extra cards (${cardStats.extraPersons} persons) · ` : ''}
                  {cardStats.ownFamily ? `${cardStats.ownFamily} own family — no card · ` : ''}
                  {cardStats.withoutType ? `${cardStats.withoutType} households not covered by any card type` : ''}
                  {!cardStats.extraCards && !cardStats.ownFamily && !cardStats.withoutType ? 'Every household is covered by a card type.' : ''}
                </div>
              </>
            )}
          </div>

          {/* 4 — money */}
          <div className="card brief-block">
            <Head k="money" extra={
              <label className="brief-tick">
                <input type="checkbox" checked={withEstimates} onChange={e => setWithEstimates(e.target.checked)} />
                Count amounts still marked “estimate”
              </label>
            } />
            {blocks.money && (
              <>
                <table className="tbl compact">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }} />
                      <th className="grow">Category</th><th className="mn-r">Items</th>
                      <th className="mn-r">Amount</th><th className="mn-r">Paid in advance</th>
                      <th className="mn-r">Balance to pay</th><th className="mn-r">Estimates</th><th>Next due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moneyRows.map(r => (
                      <tr key={r.key} className={r.on ? '' : 'brief-off'}>
                        <td><input type="checkbox" checked={r.on} onChange={() => toggleCategory(r.key)} /></td>
                        <td>{r.category}{r.fromServices ? <span className="muted" style={{ fontSize: 11.5 }}> · from Services</span> : null}</td>
                        <td className="mn-r">{r.items}</td>
                        <td className="mn-r">{money(r.total)}</td>
                        <td className="mn-r">{money(r.paid)}</td>
                        <td className="mn-r"><strong>{money(r.balance)}</strong></td>
                        <td className="mn-r">{r.estimates || ''}</td>
                        <td className={r.dueSoon > 0 ? 'mn-due' : undefined}>{r.nextDue ? fmtDate(r.nextDue) : ''}</td>
                      </tr>
                    ))}
                    {moneyRows.length === 0 && <tr><td colSpan={8} className="empty">Nothing booked yet.</td></tr>}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td /><td><strong>Total{moneyTotals.estimates ? ' (incl. estimates)' : ''}</strong></td>
                      <td />
                      <td className="mn-r"><strong>{money(moneyTotals.total)}</strong></td>
                      <td className="mn-r"><strong>{money(moneyTotals.paid)}</strong></td>
                      <td className="mn-r"><strong>{money(moneyTotals.balance)}</strong></td>
                      <td className="mn-r">{moneyTotals.estimates || ''}</td>
                      <td className={moneyTotals.dueSoon > 0 ? 'mn-due' : undefined}>
                        {moneyTotals.dueSoon > 0 ? `${money(moneyTotals.dueSoon)} in 7 days` : ''}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                <div className="muted brief-note">
                  Untick a category and it leaves the totals and the printed sheet — that is how the brief says
                  which amounts are needed and which are not. Per-person amounts follow the headcount above,
                  so they move when guests are added.
                </div>
              </>
            )}
          </div>

          {/* 5 — bookings against the live count */}
          {bookingRows.length > 0 && (
            <div className="card brief-block">
              <Head k="bookings" />
              {blocks.bookings && (
                <>
                  <table className="tbl compact">
                    <thead>
                      <tr>
                        <th>Item</th><th>Booking</th><th>For</th><th className="mn-r">Booked</th>
                        <th className="mn-r">Live now</th><th className="mn-r">Charged</th><th className="mn-r">Amount</th><th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingRows.map(r => (
                        <tr key={r.key}>
                          <td>{r.item}</td>
                          <td>{r.booking}</td>
                          <td>{r.forFn}</td>
                          <td className="mn-r">{r.booked}{r.rule === 'minimum' ? ' min' : ''}</td>
                          <td className="mn-r">{r.live} <span className="muted">{r.follows}</span></td>
                          <td className="mn-r"><strong>{r.charged}</strong></td>
                          <td className="mn-r">{money(r.amount)}</td>
                          <td className={/short/.test(r.note) ? 'mn-due' : 'muted'}>{r.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="muted brief-note">
                    A minimum (dinner booked for 100) is charged at the live count once more people come; an order
                    (288 gifts) is charged as ordered, and the shortfall shows here as guests are added.
                  </div>
                </>
              )}
            </div>
          )}

          {fnOff.size > 0 && (
            <div className="muted brief-note">
              {functions.length - fnOff.size} of {functions.length} functions are in this brief.
            </div>
          )}
          {catOff.size > 0 && <div className="muted brief-note">{catOff.size} cost {catOff.size === 1 ? 'category' : 'categories'} left out.</div>}
        </>
      )}
    </div>
  );
}
